import React, { useState, useEffect } from 'react';
import { X, Building2, User, FileText, Loader2, Search, Phone } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { waApi } from '../../services/whatsapp/waApi';

interface Department {
  id: number | string;
  nome: string;
}

interface Agent {
  id: string;
  name: string;
  email: string;
  department: string;
}

interface WaNumber {
  id: string;
  label: string;
  phone_number: string;
  phone_number_id: string;
  is_default: boolean;
  departments: string[];
}

interface Contact {
  id: string;
  name: string;
  phone: string;
  company?: string;
  /** Se definido, o cliente cai direto neste departamento sem escolher */
  fixed_department?: string | null;
}

interface OpenTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: Contact | null;
  onTicketCreated: (conversationId: string) => void;
}

export default function OpenTicketModal({
  isOpen,
  onClose,
  contact,
  onTicketCreated,
}: OpenTicketModalProps) {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedDept, setSelectedDept] = useState<string>('');
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [subject, setSubject] = useState('');
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deptSearch, setDeptSearch] = useState('');
  const [agentSearch, setAgentSearch] = useState('');
  const [waNumbers, setWaNumbers] = useState<WaNumber[]>([]);
  const [selectedNumber, setSelectedNumber] = useState<WaNumber | null>(null);
  const [, setLoadingNumbers] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchDepartments();
      // Se o contato tem departamento fixo, seleciona automaticamente
      if (contact?.fixed_department) {
        setSelectedDept(contact.fixed_department);
      } else {
        setSelectedDept('');
      }
      setSelectedAgent(null);
      setSubject('');
      setAgents([]);
      setDeptSearch('');
      setAgentSearch('');
    }
  }, [isOpen, contact?.fixed_department]);

  useEffect(() => {
    if (selectedDept) {
      fetchAgents(selectedDept);
      fetchWaNumbers(selectedDept);
      setSelectedAgent(null);
      setSelectedNumber(null);
      setAgentSearch('');
    }
  }, [selectedDept]);

  const FALLBACK_DEPTS: Department[] = [
    { id: 1, nome: 'Fiscal' },
    { id: 2, nome: 'Contabil' },
    { id: 3, nome: 'Depto. Pessoal' },
    { id: 4, nome: 'Societario' },
    { id: 5, nome: 'Consultoria' },
    { id: 6, nome: 'Atendimento' },
  ];

  const fetchDepartments = async () => {
    setLoadingDepts(true);
    try {
      const res = await waApi.get('/conversations/departments');
      const data = res.data || [];
      setDepartments(data.length > 0 ? data : FALLBACK_DEPTS);
    } catch {
      setDepartments(FALLBACK_DEPTS);
    } finally {
      setLoadingDepts(false);
    }
  };

  const fetchAgents = async (deptName: string) => {
    setLoadingAgents(true);
    try {
      const res = await waApi.get(
        `/conversations/departments/${encodeURIComponent(deptName)}/agents`,
      );
      setAgents(res.data || []);
    } catch {
      toast.error('Erro ao buscar agentes');
    } finally {
      setLoadingAgents(false);
    }
  };

  const fetchWaNumbers = async (deptName: string) => {
    setLoadingNumbers(true);
    try {
      const res = await waApi.get('/wa-numbers/available', {
        params: { department: deptName },
      });
      const nums: WaNumber[] = res.data || [];
      setWaNumbers(nums);
      // Se só tem 1, seleciona automaticamente
      if (nums.length === 1) {
        setSelectedNumber(nums[0]);
      } else {
        // Seleciona o default se existir
        const def = nums.find(n => n.is_default);
        if (def) setSelectedNumber(def);
      }
    } catch {
      setWaNumbers([]);
    } finally {
      setLoadingNumbers(false);
    }
  };

  const handleSubmit = async () => {
    if (!contact || !selectedDept) return;

    setSubmitting(true);
    try {
      const res = await waApi.post('/conversations', {
        client_id: contact.id,
        client_name: contact.name,
        client_phone: contact.phone,
        department: selectedDept,
        agent_id: selectedAgent?.id || undefined,
        agent_name: selectedAgent?.name || undefined,
        subject: subject || undefined,
        wa_number_id: selectedNumber?.id || undefined,
      });

      const convData = res.data?.data || res.data;
      const convId = convData?.id || convData?._id; // eslint-disable-line no-underscore-dangle
      toast.success('Chamado aberto com sucesso!');
      if (convId) {
        onTicketCreated(convId);
      }
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao abrir chamado');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !contact) return null;

  const filteredDepts = departments.filter(d =>
    d.nome.toLowerCase().includes(deptSearch.toLowerCase()),
  );

  const filteredAgents = agents.filter(a =>
    a.name.toLowerCase().includes(agentSearch.toLowerCase()),
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        role="button"
        tabIndex={0}
        aria-label="Fechar modal"
        onClick={onClose}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') onClose();
        }}
      />
      <div className="relative mx-4 flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Abrir Chamado</h2>
            <p className="mt-0.5 text-sm text-gray-500">
              {contact.name}
              {contact.company && <span className="text-gray-400"> - {contact.company}</span>}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {/* Departamento */}
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label
              htmlFor="ticket-dept-search"
              className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700"
            >
              <Building2 size={15} className="text-green-600" />
              Departamento
              {contact?.fixed_department && (
                <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">
                  Fixo
                </span>
              )}
            </label>
            {contact?.fixed_department ? (
              /* Departamento fixo — mostra apenas o nome */
              <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-2.5 text-sm font-medium text-green-700">
                {contact.fixed_department}
              </div>
            ) : (
              <>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    id="ticket-dept-search"
                    type="text"
                    value={deptSearch}
                    onChange={e => setDeptSearch(e.target.value)}
                    placeholder="Buscar departamento..."
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-4 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/30"
                  />
                </div>
                {loadingDepts ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 size={20} className="animate-spin text-green-600" />
                  </div>
                ) : (
                  <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-gray-200 p-1.5">
                    {filteredDepts.length === 0 ? (
                      <p className="py-3 text-center text-sm text-gray-400">
                        Nenhum departamento encontrado
                      </p>
                    ) : (
                      filteredDepts.map(dept => (
                        <button
                          key={dept.id}
                          type="button"
                          onClick={() => setSelectedDept(dept.nome)}
                          className={clsx(
                            'w-full rounded-lg px-3 py-2 text-left text-sm transition-all',
                            selectedDept === dept.nome
                              ? 'bg-green-600 font-medium text-white'
                              : 'text-gray-700 hover:bg-gray-100',
                          )}
                        >
                          {dept.nome}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Número WhatsApp */}
          {selectedDept && waNumbers.length > 1 && (
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="ticket-number-select"
                className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700"
              >
                <Phone size={15} className="text-green-600" />
                Numero WhatsApp
              </label>
              <div className="max-h-32 space-y-1 overflow-y-auto rounded-lg border border-gray-200 p-1.5">
                {waNumbers.map(num => (
                  <button
                    key={num.id}
                    type="button"
                    onClick={() => setSelectedNumber(num)}
                    className={clsx(
                      'w-full rounded-lg px-3 py-2 text-left text-sm transition-all',
                      selectedNumber?.id === num.id
                        ? 'bg-green-600 font-medium text-white'
                        : 'text-gray-700 hover:bg-gray-100',
                    )}
                  >
                    <span className="block">{num.label}</span>
                    {num.phone_number && (
                      <span
                        className={clsx(
                          'text-xs',
                          selectedNumber?.id === num.id ? 'text-green-100' : 'text-gray-400',
                        )}
                      >
                        {num.phone_number}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Agente (opcional) */}
          {selectedDept && (
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="ticket-agent-search"
                className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700"
              >
                <User size={15} className="text-green-600" />
                Atendente
                <span className="text-xs font-normal text-gray-400">(opcional)</span>
              </label>
              {agents.length > 5 && (
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    id="ticket-agent-search"
                    type="text"
                    value={agentSearch}
                    onChange={e => setAgentSearch(e.target.value)}
                    placeholder="Buscar atendente..."
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-4 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/30"
                  />
                </div>
              )}
              {loadingAgents ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 size={20} className="animate-spin text-green-600" />
                </div>
              ) : (
                <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-gray-200 p-1.5">
                  {filteredAgents.length === 0 ? (
                    <p className="py-3 text-center text-sm text-gray-400">
                      Nenhum atendente encontrado
                    </p>
                  ) : (
                    filteredAgents.map(agent => (
                      <button
                        key={agent.id}
                        type="button"
                        onClick={() => setSelectedAgent(agent)}
                        className={clsx(
                          'w-full rounded-lg px-3 py-2 text-left text-sm transition-all',
                          selectedAgent?.id === agent.id
                            ? 'bg-green-600 font-medium text-white'
                            : 'text-gray-700 hover:bg-gray-100',
                        )}
                      >
                        <span className="block">{agent.name}</span>
                        <span
                          className={clsx(
                            'text-xs',
                            selectedAgent?.id === agent.id ? 'text-green-100' : 'text-gray-400',
                          )}
                        >
                          {agent.email}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Assunto (opcional) */}
          {selectedDept && (
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="ticket-subject"
                className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700"
              >
                <FileText size={15} className="text-green-600" />
                Assunto
                <span className="text-xs font-normal text-gray-400">(opcional)</span>
              </label>
              <input
                id="ticket-subject"
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Ex: Dúvida sobre IRPF, Folha de pagamento..."
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/30"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-200"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!selectedDept || submitting}
            className={clsx(
              'flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-medium transition-all',
              !selectedDept || submitting
                ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                : 'bg-green-600 text-white shadow-sm hover:bg-green-700',
            )}
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Abrir Chamado
          </button>
        </div>
      </div>
    </div>
  );
}
